import storage, { type StorageUpdateEventDetail } from "@common/lib/storage";
import React from "react";

type UseStorageArgs<T extends {}> = {
  key: string;
  defaultValue: T;
};
const useStorage = <T extends {}>(args: UseStorageArgs<T>): [T, (state: Partial<T>) => void] => {
  const [state, setState] = React.useState(storage.get<T>(args.key) ?? args.defaultValue);

  const patchState = React.useCallback(
    (data: Partial<T>) => {
      storage.patch(args.key, data, args.defaultValue);
      setState((x) => ({ ...x, ...data }));
    },
    [args.key, args.defaultValue]
  );

  React.useEffect(() => {
    if (!storage.get<T>(args.key)) {
      patchState({});
    }

    const onChange = (event: CustomEvent) => {
      const { key, value } = event.detail as StorageUpdateEventDetail;
      if (key !== args.key) return;

      setState(JSON.parse(value));
    };

    window.addEventListener(args.key, onChange, false);

    return () => {
      window.removeEventListener(args.key, onChange, false);
    };
  }, [args.key, patchState]);

  return [state, patchState];
};
export default useStorage;
